
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Shield, 
  AlertTriangle, 
  TrendingUp, 
  RefreshCw,
  Database,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { CertificateAuditService } from '@/services/certificateAuditService';
import { DataConsistencyService } from '@/services/dataConsistencyService';
import { toast } from '@/components/ui/sonner';

const CertificateAuditDashboard: React.FC = () => {
  const [auditStats, setAuditStats] = useState<any>(null);
  const [auditEntries, setAuditEntries] = useState<any[]>([]);
  const [consistencyReports, setConsistencyReports] = useState<any[]>([]);
  const [trends, setTrends] = useState<any>({ dailyStats: [], topFailureReasons: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAuditData();
  }, []);

  const loadAuditData = async () => {
    setLoading(true);
    try {
      const [stats, entries, reports, trendsData] = await Promise.all([
        CertificateAuditService.getCertificateAuditStats(),
        CertificateAuditService.getCertificateAuditEntries({ limit: 50 }),
        DataConsistencyService.getConsistencyReports({ resolved: false }),
        CertificateAuditService.getCertificateValidationTrends()
      ]);

      setAuditStats(stats);
      setAuditEntries(entries);
      setConsistencyReports(reports);
      setTrends(trendsData);
    } catch (error) {
      console.error('Error loading audit data:', error);
      toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  const runConsistencyCheck = async () => {
    try {
      toast.info('Running comprehensive data validation...');
      const result = await DataConsistencyService.runComprehensiveValidation();
      
      if (result.totalIssues > 0) {
        toast.warning(`Found ${result.totalIssues} data inconsistencies (${result.criticalIssues} critical)`);
      } else {
        toast.success('Data validation completed - no issues found');
      }
      
      await loadAuditData();
    } catch (error) {
      console.error('Error running consistency check:', error);
      toast.error('Failed to run consistency check');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p>Loading audit data...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Attempts</p>
                <p className="text-2xl font-bold">{auditStats?.totalAttempts || 0}</p>
              </div>
              <Database className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                <p className="text-2xl font-bold text-green-600">
                  {auditStats?.successRate ? `${auditStats.successRate}%` : '0%'}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Failed Attempts</p>
                <p className="text-2xl font-bold text-red-600">{auditStats?.failedAttempts || 0}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Data Issues</p>
                <p className="text-2xl font-bold text-orange-600">{consistencyReports.length}</p>
                {consistencyReports.length > 0 && (
                  <Badge variant="outline" className="mt-1 text-orange-600">
                    Needs Attention
                  </Badge>
                )}
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="recent-attempts" className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="recent-attempts">Recent Attempts</TabsTrigger>
            <TabsTrigger value="data-consistency">Data Consistency</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <Button onClick={runConsistencyCheck} variant="outline" size="sm">
              <Database className="h-4 w-4 mr-2" />
              Run Validation
            </Button>
            <Button onClick={loadAuditData} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        <TabsContent value="recent-attempts">
          <Card>
            <CardHeader>
              <CardTitle>Recent Certificate Attempts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {auditEntries.map(entry => (
                  <div key={entry.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={
                          entry.eligibilityStatus === 'eligible' ? 'default' :
                          entry.eligibilityStatus === 'not_eligible' ? 'secondary' : 'destructive'
                        }>
                          {entry.eligibilityStatus.replace('_', ' ')}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {new Date(entry.attemptTimestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="font-medium">{entry.courseId}</p>
                      <p className="text-sm text-muted-foreground">
                        {entry.completedModules}/{entry.totalModules} modules completed
                        {entry.quizAverage && ` • ${entry.quizAverage.toFixed(1)}% average`}
                      </p>
                      {entry.rejectionReason && (
                        <p className="text-sm text-red-600 mt-1">{entry.rejectionReason}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {entry.certificateIssued ? (
                        <Badge variant="default">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Issued
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <Clock className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="data-consistency">
          <Card>
            <CardHeader>
              <CardTitle>Data Consistency Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {consistencyReports.map(report => (
                  <div key={report.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={
                          report.severityLevel === 'critical' ? 'destructive' :
                          report.severityLevel === 'high' ? 'destructive' :
                          report.severityLevel === 'medium' ? 'secondary' : 'outline'
                        }>
                          {report.severityLevel}
                        </Badge>
                        <span className="font-medium">{report.reportType.replace('_', ' ')}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Course: {report.affectedCourseId || 'N/A'} • 
                        Module: {report.affectedModuleId || 'N/A'}
                      </p>
                      <p className="text-sm mt-1">
                        {JSON.stringify(report.inconsistencyDetails).substring(0, 100)}...
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => DataConsistencyService.resolveConsistencyReport(report.id, 'admin')}
                    >
                      Mark Resolved
                    </Button>
                  </div>
                ))}
                {consistencyReports.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-600" />
                    <p>No data consistency issues found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends">
          <Card>
            <CardHeader>
              <CardTitle>Validation Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Top Failure Reasons */}
                <div>
                  <h4 className="font-medium mb-3">Top Failure Reasons</h4>
                  <div className="space-y-2">
                    {trends.topFailureReasons.map((reason: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted rounded">
                        <span className="text-sm">{reason.reason}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{reason.count} cases</Badge>
                          <span className="text-sm text-muted-foreground">
                            {reason.percentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CertificateAuditDashboard;
